import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Select, Button, Space, Switch, Card, Typography, Divider, Collapse, Alert, Spin } from 'antd';
import { PlusOutlined, MinusCircleOutlined, TeamOutlined, ApartmentOutlined } from '@ant-design/icons';
import { validatePhoneForBlur, isValidPhoneTyping } from '../../utils/phone';
import { useEditingGuard } from '../../utils/editingGuard';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

// Comprehensive CMT roles matching the platform's role system
const CMT_ROLES = [
  // Leadership roles
  { value: 'cmt_leader', label: 'CMT Leader' },
  { value: 'cmt_leader_alt', label: 'CMT Leader (Alternate)' },
  { value: 'cmt_admin', label: 'CMT Admin' },
  
  // Functional specialist roles  
  { value: 'crisis_manager', label: 'Crisis Manager' },
  { value: 'safety_officer', label: 'Safety Officer' },
  { value: 'comms_media', label: 'Communications/Media/PR' },
  { value: 'legal_compliance', label: 'Legal/Compliance' },
  { value: 'safety_security', label: 'Safety/Security' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'talent_hr', label: 'Talent/HR' },
  { value: 'it_tech', label: 'IT/Tech' },
  { value: 'finance', label: 'Finance' },
  { value: 'client_service', label: 'Client Service' },
  
  // Catch-all
  { value: 'other', label: 'Other' }
];

export default function CrisisManagementTeam({ 
  form, 
  isAdminMode = false, 
  organizationData = null, 
  onSave = null,
  onMembersChange = null
}) {
  // Create internal form only if not passed from parent (onboarding mode)
  const [internalForm] = Form.useForm();
  const formInstance = form || internalForm;
  const [cmtStructure, setCmtStructure] = useState(null);
  const [isMultiCmt, setIsMultiCmt] = useState(false);
  
  // 🚨 CRITICAL FIX: Prevent form re-seeding that causes email reversion
  const didInitForm = useRef(false);
  const lastOrgId = useRef(null);

  // 📱 Phone field editing guard and error tracking
  const editingGuard = useEditingGuard();
  const [phoneErrors, setPhoneErrors] = useState({});
  
  // 📱 Initialize editing guard on mount
  useEffect(() => {
    editingGuard.activate();
    return () => editingGuard.reset();
  }, [editingGuard]);

  // 📱 Phone field helper functions
  const getPhoneFieldPath = (cmtId, memberIndex, fieldName = 'phone') => {
    if (isMultiCmt && cmtId) {
      return ['multiCmtMembers', cmtId, memberIndex, fieldName];
    } else {
      return ['cmtMembers', memberIndex, fieldName];
    }
  };

  const handlePhoneFocus = (cmtId, memberIndex, fieldName = 'phone') => {
    const fieldPath = getPhoneFieldPath(cmtId, memberIndex, fieldName);
    editingGuard.setEditing(fieldPath, true);
    
    // Clear any existing error for this field
    const errorKey = fieldPath.join('.');
    setPhoneErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  const handlePhoneBlur = (cmtId, memberIndex, fieldName = 'phone') => {
    const fieldPath = getPhoneFieldPath(cmtId, memberIndex, fieldName);
    const currentValue = formInstance.getFieldValue(fieldPath);
    const errorKey = fieldPath.join('.');
    
    if (currentValue) {
      const validation = validatePhoneForBlur(currentValue, 'AU');
      
      if (validation.isValid && validation.e164) {
        // Valid phone - convert to E.164
        formInstance.setFieldValue(fieldPath, validation.e164);
        setPhoneErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      } else if (validation.error) {
        // Invalid phone - keep user text and show error
        setPhoneErrors(prev => ({
          ...prev,
          [errorKey]: validation.error
        }));
      }
    }
    
    editingGuard.setEditing(fieldPath, false);
    
    // Trigger form change after blur processing
    setTimeout(() => handleFormChange(), 100);
  };

  const validatePhoneOnSubmit = () => {
    const formValues = formInstance.getFieldsValue();
    let hasPhoneErrors = false;
    const newPhoneErrors = {};

    // Check all phone fields for validity
    const checkPhoneField = (value, fieldPath) => {
      if (value) {
        const validation = validatePhoneForBlur(value, 'AU');
        if (!validation.isValid) {
          hasPhoneErrors = true;
          newPhoneErrors[fieldPath.join('.')] = validation.error || 'Invalid phone number';
        }
      }
    };

    if (isMultiCmt && formValues.multiCmtMembers) {
      Object.keys(formValues.multiCmtMembers).forEach(cmtId => {
        const members = formValues.multiCmtMembers[cmtId];
        if (Array.isArray(members)) {
          members.forEach((member, index) => {
            if (member.phone) {
              checkPhoneField(member.phone, ['multiCmtMembers', cmtId, index, 'phone']);
            }
            if (member.backupPhone) {
              checkPhoneField(member.backupPhone, ['multiCmtMembers', cmtId, index, 'backupPhone']);
            }
          });
        }
      });
    } else if (formValues.cmtMembers) {
      formValues.cmtMembers.forEach((member, index) => {
        if (member.phone) {
          checkPhoneField(member.phone, ['cmtMembers', index, 'phone']);
        }
        if (member.backupPhone) {
          checkPhoneField(member.backupPhone, ['cmtMembers', index, 'backupPhone']);
        }
      });
    }

    setPhoneErrors(newPhoneErrors);
    return !hasPhoneErrors;
  };

  // Helper function to extract CMTs from hierarchy data - MOVED BEFORE useEffect
  const extractCMTsFromHierarchy = (hierarchyData) => {
    if (!hierarchyData || !hierarchyData.root) return [];
    
    const cmts = [];
    
    // Recursive function to traverse hierarchy
    const traverse = (node, level = 0) => {
      cmts.push({
        key: node.id || `cmt_${cmts.length}`,
        name: node.name,
        type: node.type || 'CMT',
        scope: node.scope || ''
      });
      
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => traverse(child, level + 1));
      }
    };
    
    traverse(hierarchyData.root);
    return cmts;
  };

  /**
   * 🔄 Handle form value changes and notify parent in admin mode
   * 🚨 DEBOUNCED to prevent autocomplete race conditions
   * 📱 FILTERED to prevent phone field overwrites during editing
   */
  const handleFormChange = useCallback(() => {
    if (isAdminMode && onMembersChange) {
      const rawFormValues = formInstance.getFieldsValue();
      
      // 📱 Filter out phone fields that are currently being edited
      const formValues = editingGuard.filterEditingFields(rawFormValues);
        
      console.log('🔍 FORM CHANGE DEBUG - Raw form values:', rawFormValues);
      console.log('🔍 FORM CHANGE DEBUG - Filtered form values:', formValues);
        
        // Extract member data from form
        let members = [];
        
        if (formValues.multiCmtMembers) {
          // Multi-CMT format: convert nested structure to flat array
          Object.entries(formValues.multiCmtMembers).forEach(([cmtKey, cmtMembers]) => {
            console.log(`🔍 Processing CMT ${cmtKey}:`, cmtMembers);
            if (Array.isArray(cmtMembers)) {
              cmtMembers.forEach((member, index) => {
                if (member && (member.fullName || member.email)) {
                  console.log(`✅ Adding member ${index} for CMT ${cmtKey}:`, member);
                  members.push({
                    ...member,
                    cmtId: cmtKey,
                    cmtKey: cmtKey
                  });
                } else {
                  console.log(`⚠️ Skipping empty member ${index} for CMT ${cmtKey}:`, member);
                }
              });
            } else {
              console.log(`⚠️ CMT ${cmtKey} members is not an array:`, cmtMembers);
            }
          });
        } else if (formValues.cmtMembers) {
          // Single CMT format
          members = formValues.cmtMembers.filter(member => 
            member && (member.fullName || member.email)
          );
        }
        
        console.log('🎯 FINAL extracted members:', members);
        onMembersChange(members);
    }
  }, [isAdminMode, onMembersChange, formInstance]);

  // Load CMT structure configuration from form or organization data
  useEffect(() => {
    if (isAdminMode && organizationData) {
      // 🚨 CRITICAL FIX: Don't reload if user is actively editing
      // Check if form has any field values that indicate user input OR if it's been recently changed
      const currentFormValues = formInstance.getFieldsValue();
      const hasUserInput = currentFormValues.multiCmtMembers && Object.keys(currentFormValues.multiCmtMembers).length > 0;
      
      // 🚨 AGGRESSIVE PROTECTION: Check if any field has focus (user is typing)
      const activeElement = document.activeElement;
      const isUserTyping = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA');
      
      // 🚨 DISABLED: This was blocking Save & Sync button
      if (false && (hasUserInput || isUserTyping)) {
        console.log('⚠️ PREVENTING FORM RELOAD: User has input in form or is actively typing - protecting form state', {
          hasUserInput,
          isUserTyping,
          activeElement: activeElement?.tagName,
          activeElementType: activeElement?.type
        });
        // Still update structure but don't overwrite form values
        const hasRealTimeCmts = !!(organizationData.cmts && Array.isArray(organizationData.cmts) && organizationData.cmts.length > 0);
        const hasHierarchyData = !!(organizationData.hierarchyData && organizationData.hierarchyData.root);
        const hasMultipleSubCMTs = !!(organizationData.cmts && Object.keys(organizationData.cmts).length > 1);
        
        setIsMultiCmt(hasRealTimeCmts || hasHierarchyData || hasMultipleSubCMTs);
        
        if (hasRealTimeCmts) {
          const cmtList = organizationData.cmts.map(cmt => ({
            key: cmt.id,
            name: cmt.name,
            type: cmt.type || 'CMT',
            scope: cmt.description || cmt.scope || ''
          }));
          setCmtStructure({ cmtList });
        } else if (hasHierarchyData) {
          const cmtList = extractCMTsFromHierarchy(organizationData.hierarchyData);
          setCmtStructure({ cmtList });
        }
        return; // Don't proceed to setFieldsValue
      }
      
      // Admin mode: Load data from existing organization
      // Check if organization has multi-CMT structure
      const hasRealTimeCmts = !!(organizationData.cmts && Array.isArray(organizationData.cmts) && organizationData.cmts.length > 0);
      const hasHierarchyData = !!(organizationData.hierarchyData && organizationData.hierarchyData.root);
      const hasMultipleSubCMTs = !!(organizationData.cmts && Object.keys(organizationData.cmts).length > 1);
      
      setIsMultiCmt(hasRealTimeCmts || hasHierarchyData || hasMultipleSubCMTs);
      
      if (hasRealTimeCmts) {
        // Use real-time CMT data from admin interface (priority)
        const cmtList = organizationData.cmts.map(cmt => ({
          key: cmt.id,
          name: cmt.name,
          type: cmt.type || 'CMT',
          scope: cmt.description || cmt.scope || ''
        }));
        setCmtStructure({ cmtList });
        
        if (organizationData.multiCmtMembers) {
          // 🚨 CRITICAL FIX: Gate form seeding to prevent email reversion
          const orgIdChanged = lastOrgId.current !== organizationData.id;
          if (!didInitForm.current || orgIdChanged) {
            console.log('🔧 Loading multi-CMT members into form - FIRST INIT OR ORG CHANGED:', { 
              didInit: didInitForm.current, 
              orgIdChanged,
              orgId: organizationData.id 
            });
            
            // 🚨 DEBUG: Log what we're about to set
            console.log('🔍 multiCmtMembers data being set:', JSON.stringify(organizationData.multiCmtMembers, null, 2));
            
            if (orgIdChanged) {
              formInstance.resetFields();
              lastOrgId.current = organizationData.id;
            }
            
            const fieldsToSet = editingGuard.filterEditingFields({ multiCmtMembers: organizationData.multiCmtMembers });
            formInstance.setFieldsValue(fieldsToSet);
            didInitForm.current = true;
          } else {
            console.log('⚠️ SKIPPING FORM SEEDING - Already initialized for this org');
          }
        }
      } else if (hasHierarchyData) {
        // Fallback to hierarchy data
        const cmtList = extractCMTsFromHierarchy(organizationData.hierarchyData);
        setCmtStructure({ cmtList });
        
        if (organizationData.multiCmtMembers) {
          // 🚨 CRITICAL FIX: Gate form seeding to prevent email reversion
          const orgIdChanged = lastOrgId.current !== organizationData.id;
          if (!didInitForm.current || orgIdChanged) {
            console.log('🔧 Loading hierarchy-based members into form - FIRST INIT OR ORG CHANGED:', { 
              didInit: didInitForm.current, 
              orgIdChanged,
              orgId: organizationData.id 
            });
            
            // 🚨 DEBUG: Log what we're about to set
            console.log('🔍 multiCmtMembers data being set (hierarchy):', JSON.stringify(organizationData.multiCmtMembers, null, 2));
            
            if (orgIdChanged) {
              formInstance.resetFields();
              lastOrgId.current = organizationData.id;
            }
            
            const fieldsToSet = editingGuard.filterEditingFields({ multiCmtMembers: organizationData.multiCmtMembers });
            formInstance.setFieldsValue(fieldsToSet);
            didInitForm.current = true;
          } else {
            console.log('⚠️ SKIPPING HIERARCHY FORM SEEDING - Already initialized for this org');
          }
        }
      } else {
        // Single CMT organization
        setCmtStructure(null);
        if (organizationData.cmtMembers) {
          // 🚨 CRITICAL FIX: Gate form seeding to prevent email reversion
          const orgIdChanged = lastOrgId.current !== organizationData.id;
          if (!didInitForm.current || orgIdChanged) {
            console.log('🔧 Loading CMT members into form - FIRST INIT OR ORG CHANGED:', { 
              didInit: didInitForm.current, 
              orgIdChanged,
              orgId: organizationData.id 
            });
            
            // 🚨 DEBUG: Log what we're about to set
            console.log('🔍 cmtMembers data being set:', JSON.stringify(organizationData.cmtMembers, null, 2));
            
            if (orgIdChanged) {
              formInstance.resetFields();
              lastOrgId.current = organizationData.id;
            }
            
            const fieldsToSet = editingGuard.filterEditingFields({ cmtMembers: organizationData.cmtMembers });
            formInstance.setFieldsValue(fieldsToSet);
            didInitForm.current = true;
          } else {
            console.log('⚠️ SKIPPING CMT MEMBERS SEEDING - Already initialized for this org');
          }
        }
      }
    } else {
      // Onboarding mode: Load data from form
      const structureType = formInstance.getFieldValue('cmtStructureType');
      const hierarchyData = formInstance.getFieldValue('hierarchyData');
      
      setIsMultiCmt(structureType === 'multi');
      
      if (structureType === 'multi' && hierarchyData) {
        // Convert hierarchy data to simple CMT list for member assignment
        const cmtList = extractCMTsFromHierarchy(hierarchyData);
        setCmtStructure({ cmtList });
      }
    }
  }, [formInstance, isAdminMode, organizationData]);

  /**
   * 🎯 Render member assignment form for a specific CMT
   */
  const renderCMTMemberForm = (cmtName, cmtKey, memberPrefix = 'cmtMembers') => {
    return (
      <Form.List name={[memberPrefix, cmtKey]}>
        {(fields, { add, remove }) => {
  return (
          <>
            {fields.map(({ key, name, ...restField }) => (
            <div key={key} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>Team Member #{key + 1}</Text>
                  <Button 
                    type="text" 
                    danger 
                    onClick={() => {
                      remove(name);
                      setTimeout(() => handleFormChange(), 100);
                    }} 
                    icon={<MinusCircleOutlined />}
                    size="small"
                  >
                    Remove
                  </Button>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'fullName']}
                    label="Full Name"
                      rules={[{ required: true, message: 'Name required' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input 
                        placeholder="Enter full name" 
                        size="small" 
                        onChange={() => setTimeout(() => handleFormChange(), 100)}
                      />
                  </Form.Item>
                  </div>

                  <div style={{ flex: '1 1 200px' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'email']}
                    label="Email"
                    rules={[
                        { required: true, message: 'Email required' },
                        { type: 'email', message: 'Valid email required' }
                      ]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input 
                        placeholder="Enter email" 
                        size="small" 
                        autoComplete="off"
                        name={`email-${key}`}
                      />
                  </Form.Item>
                  </div>

                  <div style={{ flex: '1 1 150px' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'phone']}
                      label="Mobile"
                      rules={[
                        { required: true, message: 'Phone required' },
                        {
                          validator: async (_, value) => {
                            if (!value) return Promise.resolve();
                            const fieldPath = getPhoneFieldPath(cmtKey, name, 'phone');
                            const errorKey = fieldPath.join('.');
                            if (phoneErrors[errorKey]) {
                              return Promise.reject(phoneErrors[errorKey]);
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      validateStatus={(() => {
                        const fieldPath = getPhoneFieldPath(cmtKey, name, 'phone');
                        const errorKey = fieldPath.join('.');
                        return phoneErrors[errorKey] ? 'error' : '';
                      })()}
                      help={(() => {
                        const fieldPath = getPhoneFieldPath(cmtKey, name, 'phone');
                        const errorKey = fieldPath.join('.');
                        return phoneErrors[errorKey] || '';
                      })()}
                      style={{ marginBottom: 8 }}
                    >
                      <Input 
                        placeholder="+61412345678" 
                        size="small" 
                        onFocus={() => handlePhoneFocus(cmtKey, name, 'phone')}
                        onBlur={() => handlePhoneBlur(cmtKey, name, 'phone')}
                        allowClear 
                      />
                  </Form.Item>
                  </div>

                  <div style={{ flex: '1 1 150px' }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'cmtRole']}
                      label="Role"
                      rules={[{ required: true, message: 'Role required' }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Select 
                        placeholder="Select role" 
                        size="small"
                        onChange={() => setTimeout(() => handleFormChange(), 100)}
                      >
                      {CMT_ROLES.map(role => (
                          <Select.Option key={role.value} value={role.value}>
                            {role.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  </div>
                </div>

                  <Form.Item
                    {...restField}
                    name={[name, 'hasBackup']}
                    label="Has Backup Contact"
                    valuePropName="checked"
                  style={{ marginBottom: 8 }}
                  >
                  <Switch size="small" />
                  </Form.Item>

                {formInstance.getFieldValue([memberPrefix, cmtKey, name, 'hasBackup']) && (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'backupName']}
                        label="Backup Name"
                        rules={[{ required: true, message: 'Backup name required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Backup contact name" size="small" />
                      </Form.Item>
                    </div>
                    <div style={{ flex: 1 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'backupPhone']}
                        label="Backup Mobile"
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!value) return Promise.resolve();
                              const fieldPath = getPhoneFieldPath(cmtKey, name, 'backupPhone');
                              const errorKey = fieldPath.join('.');
                              if (phoneErrors[errorKey]) {
                                return Promise.reject(phoneErrors[errorKey]);
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        validateStatus={(() => {
                          const fieldPath = getPhoneFieldPath(cmtKey, name, 'backupPhone');
                          const errorKey = fieldPath.join('.');
                          return phoneErrors[errorKey] ? 'error' : '';
                        })()}
                        help={(() => {
                          const fieldPath = getPhoneFieldPath(cmtKey, name, 'backupPhone');
                          const errorKey = fieldPath.join('.');
                          return phoneErrors[errorKey] || '';
                        })()}
                        style={{ marginBottom: 0 }}
                      >
                        <Input 
                          placeholder="+61412345678" 
                          size="small" 
                          onFocus={() => handlePhoneFocus(cmtKey, name, 'backupPhone')}
                          onBlur={() => handlePhoneBlur(cmtKey, name, 'backupPhone')}
                          allowClear 
                        />
                      </Form.Item>
                    </div>
                  </div>
                )}
                </Space>
              </div>
            ))}

          <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="dashed"
              onClick={() => {
                add();
                // Notify parent of member changes
                setTimeout(() => handleFormChange(), 100);
              }}
                block
                icon={<PlusOutlined />}
              size="small"
              >
              Add Member to {cmtName}
              </Button>
            </Form.Item>
          </>
          );
        }}
      </Form.List>
    );
  };

  /**
   * 🏗️ Render multi-CMT assignment interface
   */
  const renderMultiCMTAssignment = () => {
    if (!cmtStructure || !cmtStructure.cmtList) {
      return (
        <Alert
          message="No CMT Structure Found"
          description="Please configure your crisis management teams first."
          type="error"
          showIcon
        />
      );
    }

    const cmtList = cmtStructure.cmtList;

    if (cmtList.length === 0) {
      return (
        <Alert
          message="No CMTs Configured"
          description="Please go back to the CMT Structure step and configure your crisis management teams."
          type="warning"
          showIcon
        />
      );
    }

    return (
      <div>
        <Collapse defaultActiveKey={cmtList.map(cmt => cmt.key)} ghost>
          {cmtList.map((cmt) => (
            <Panel 
              key={cmt.key}
              header={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ApartmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <span style={{ fontWeight: 'bold' }}>{cmt.name}</span>
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '12px', 
                      color: '#666',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {cmt.type}
                    </span>
                  </div>
                </div>
              }
            >
              {(cmt.scope || cmt.responsibility || cmt.boundaries || cmt.focus) && (
                <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    <strong>Scope:</strong> {cmt.scope || cmt.responsibility || cmt.boundaries || cmt.focus}
                  </Text>
                </div>
              )}
              
              {renderCMTMemberForm(cmt.name, cmt.key, 'multiCmtMembers')}
            </Panel>
          ))}
        </Collapse>
      </div>
    );
  };

  /**
   * 🎯 Render single CMT assignment interface (backward compatibility)
   */
  const renderSingleCMTAssignment = () => (
    <div>
      <Title level={4} style={{ marginBottom: '16px' }}>
        <TeamOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
        Crisis Management Team Members
      </Title>
      
      <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
        Add the key personnel who will be part of your crisis management team. 
        Ensure you have at least one Leader and consider backup contacts for critical roles.
      </Paragraph>

      {renderCMTMemberForm('Crisis Management Team', 'default', 'cmtMembers')}
    </div>
  );

  /**
   * 💾 Handle saving CMT member assignments in admin mode
   * 📱 Includes phone validation before save
   */
  const handleAdminSave = async () => {
    if (!isAdminMode || !onSave) return;
    
    try {
      // 📱 Validate phone numbers before save
      const isPhoneValid = validatePhoneOnSubmit();
      if (!isPhoneValid) {
        console.warn('📱 Save blocked: Invalid phone numbers detected');
        return;
      }

      const formValues = formInstance.getFieldsValue();
      console.log('💾 Saving CMT form data:', formValues);
      await onSave(formValues);
    } catch (error) {
      console.error('Error saving CMT member assignments:', error);
    }
  };

  // Check if we're still loading organization data in admin mode
  const isDataLoading = isAdminMode && !organizationData;

  // Show loading state in admin mode until data loads
  if (isDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading CMT structure...</div>
      </div>
    );
  }

  return (
    <div>
      
      {isMultiCmt ? (
        <div>
          {!isAdminMode && (
            <>
              <Title level={3} style={{ marginBottom: '8px', textAlign: 'center' }}>
                <ApartmentOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                Crisis Management Team Assignment (MULTI-CMT MODE)
              </Title>
              
              <Paragraph style={{ textAlign: 'center', color: '#666', fontSize: '16px', marginBottom: '32px' }}>
                Assign team members to each crisis management team based on your organizational structure.
              </Paragraph>
            </>
          )}

          {renderMultiCMTAssignment()}
        </div>
      ) : (
        <div>
          {renderSingleCMTAssignment()}
        </div>
      )}
      

    </div>
  );
} 